/**
 * PIN validity rules, shared by the server gate (core/tokens.ts validPin)
 * and the client check in PinSetup, so the browser refuses the same PINs
 * the server would. Pure string logic only: this module is part of the
 * client bundle and must never import node built-ins.
 */

/** Length gate: 6-10 digits, nothing else (raised from 4-6 in the
 *  2026-07-21 review round: a 4-digit PIN in front of resident PII is too
 *  guessable even with the fail cap). */
export function pinLengthOk(pin: string): boolean {
  return /^\d{6,10}$/.test(pin);
}

/** Quality gate (round-3 review, P1): one repeated digit (000000) and
 *  whole-string strict +1/-1 runs (123456, 987654) are the PINs people
 *  actually pick, so they are refused outright. A run that wraps (...890)
 *  or breaks anywhere is not a run and stays allowed. */
export function weakPin(pin: string): boolean {
  const steps = new Set<number>();
  for (let i = 1; i < pin.length; i++) {
    steps.add(pin.charCodeAt(i) - pin.charCodeAt(i - 1));
  }
  return steps.size === 1 && (steps.has(0) || steps.has(1) || steps.has(-1));
}

/** A valid PIN passes both gates. */
export function validPin(pin: string): boolean {
  return pinLengthOk(pin) && !weakPin(pin);
}
